import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../../ui/toast';
import {
  createJobPostUpload,
  getJobPost,
  updateJobPostUpload,
} from '../../../../../../../utils/api_service/JobPosting';
import { toISODateString } from '../../../../../../../utils/helpers/dateUtils';
import { RootState } from '../../../../../../../utils/redux_store/store';
import { JobPostingTabData } from '../../../../../../../utils/types/JobOpenings';
export interface IJobPostingPreview {
  jobPostingData: JobPostingTabData;
  jobPostId: string;
  setJobPostId: (value: ((prevState: string) => string) | string) => void;
  setStages: (
    value:
      | ((
          prevState: {
            currentSubStageIndex: number;
            subStages: { label: string; status: string }[];
            label: string;
            status: string;
          }[],
        ) => {
          currentSubStageIndex: number;
          subStages: { label: string; status: string }[];
          label: string;
          status: string;
        }[])
      | {
          currentSubStageIndex: number;
          subStages: { label: string; status: string }[];
          label: string;
          status: string;
        }[],
  ) => void;
  liveHtml?: string;
  hidePublishBanner?: boolean;
}

export const useJobPostingPreview = (props: IJobPostingPreview) => {
  const { jobPostingData, jobPostId, setJobPostId, setStages, liveHtml } = props;
  const { id } = useParams<{ id: string }>();
  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const { i18n } = useTranslation();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isCreatingPost, setIsCreatingPost] = useState<boolean>(false);
  const [previewHtml, setPreviewHtml] = useState<string>(liveHtml ?? jobPostingData.htmlTemplate ?? '');

  // Auto-switch job description language to match the UI locale when a translation exists.
  useEffect(() => {
    if (liveHtml !== undefined) {
      setPreviewHtml(liveHtml);
      return;
    }
    const uiLang = i18n.language?.split('-')[0] ?? 'en';
    const available = jobPostingData.availableLanguages ?? [];
    if (uiLang !== 'en' && available.includes(uiLang) && jobPostId) {
      getJobPost(jobPostId, uiLang)
        .then((res) => res.json())
        .then((data) => {
          const post = data.jobPosting || data;
          if (post.htmlTemplate) setPreviewHtml(post.htmlTemplate);
        })
        .catch(() => setPreviewHtml(jobPostingData.htmlTemplate ?? ''));
    } else {
      setPreviewHtml(jobPostingData.htmlTemplate ?? '');
    }
  }, [jobPostId, i18n.language, jobPostingData.availableLanguages, jobPostingData.htmlTemplate, liveHtml]);
  const url =
    jobPostingData.videoFilename instanceof File
      ? URL.createObjectURL(jobPostingData.videoFilename)
      : typeof jobPostingData.videoFilename === 'string'
        ? jobPostingData.videoFilename
        : null;

  const jobPostUrl = `${window.location.origin}/jobs/${jobPostId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jobPostUrl);
      alert('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy: ', { position: 'bottom-center' });
    }
  };

  const createPost = async () => {
    if (isCreatingPost) return; // Prevent multiple clicks

    setIsCreatingPost(true);
    try {
      if (jobPostId !== '') {
        const updateEmailResponse = await updateJobPostUpload(
          jobPostId,
          id,
          jobPostingData.department,
          jobPostingData.jobTitle,
          jobPostingData.workType,
          jobPostingData.jsonTemplate || '',
          jobPostingData.htmlTemplate || '',
          toISODateString(jobPostingData.startDate),
          toISODateString(jobPostingData.endDate),
          jobPostingData.active,
          jobPostingData.location,
          jobPostingData.videoFilename instanceof File
            ? jobPostingData.videoFilename
            : undefined,
          jobPostingData.videoDescription
            ? jobPostingData.videoDescription
            : undefined,
          jobPostingData.videoTitle ? jobPostingData.videoTitle : undefined,
        );

        if (updateEmailResponse.ok) {
          toast.success('Job Post Successfully Updated!', {
            position: 'bottom-center',
          });
        } else {
          throw new Error('Error updating job template');
        }
      } else {
        const updateEmailResponse = await createJobPostUpload(
          id,
          jobPostingData.department,
          jobPostingData.jobTitle,
          jobPostingData.workType,
          jobPostingData.jsonTemplate || '',
          jobPostingData.htmlTemplate || '',
          toISODateString(jobPostingData.startDate),
          toISODateString(jobPostingData.endDate),
          jobPostingData.active,
          jobPostingData.location,
          jobPostingData.videoFilename instanceof File
            ? jobPostingData.videoFilename
            : undefined,
          jobPostingData.videoDescription
            ? jobPostingData.videoDescription
            : undefined,
          jobPostingData.videoTitle ? jobPostingData.videoTitle : undefined,
        );

        const response = await updateEmailResponse.json();
        setJobPostId(response.id);
        setStages((prevStages: any[]) =>
          prevStages.map((stage) =>
            stage.label === 'Job Post'
              ? { ...stage, status: 'In Progress' } // Replace "NEW_STATUS" with the desired status
              : stage,
          ),
        );

        if (updateEmailResponse.ok) {
          // Show success notification with URL completion message
          toast.success(
            'Job Post Created Successfully! Your job URL is ready to share',
            {
              position: 'bottom-center',
              autoClose: 5000,
            },
          );
        } else {
          throw new Error('Error updating job template');
        }
      }
    } catch (error) {
      toast.error((error as Error).message, { position: 'bottom-center' });
    } finally {
      setIsCreatingPost(false);
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying); // Toggle play/pause state
    }
  };
  return {
    id,
    jobPostUrl,
    handleCopy,
    createPost,
    url,
    videoRef,
    handlePlayPause,
    isPlaying,
    companyLogo,
    isCreatingPost,
    previewHtml,
  };
};
